'use client';

import geoContract from '@bells/api/geo/frontend';
import { useLanguage } from '@bells/core/i18n';
import { cn } from '@bells/core/lib/utils';
import { generateRestClient } from '@bells/core/rest/react';
import type { Locale } from '@bells/i18n/runtime';
import {
  FoundLocationReturn,
  type FoundLocation,
} from '@bells/schema/models/mapbox';
import { Command as CommandPrimitive } from 'cmdk';
import { endOfDay, isBefore } from 'date-fns';
import { isEmpty } from 'lodash';
import {
  CircleX,
  Hotel,
  Hourglass,
  House,
  Loader2,
  MapPin,
  SearchIcon,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { toast } from '../utils/use-toast';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from './command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { Label } from './label';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

import { format, startOfDay } from 'date-fns';

import { useAppSettings } from '@bells/core';
import {
  attendantSearchChooseDuration,
  crudNotFound,
  durationDays,
  durationHours,
  locationSearchChange,
  locationSearchDurationDesktop,
  locationSearchDurationMobile,
  locationSearchMyAddress,
  locationSearchPlaceholder,
  locationSearchSubmit,
  locationSearchSuggestion,
  locationSearchWhenDesktop,
  locationSearchWhenMobile,
  locationSearchWhereDesktop,
  locationSearchWhereMobile,
  locationSearchWherePlaceholder,
} from '@bells/i18n';
import { PriceArrayKeys } from '@bells/schema/models/attendant';
import { CalendarIcon, Check, Clock } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { Alert, AlertTitle } from '../components/alert';
import { Spinner } from '../components/spinner';
import { useMediaQuery } from '../hooks/use-media-query';
import { Calendar } from './calendar';
import { CommandItem } from './command';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from './drawer';
import { FlagIcon } from './flag-icon';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
const hoursInFuture = 3;
const allowedDurations = [
  ...PriceArrayKeys,
  24 * 3,
  24 * 4,
  24 * 5,
  24 * 6,
  24 * 7,
  24 * 8,
  24 * 9,
  24 * 10,
  24 * 11,
  24 * 12,
  24 * 13,
  24 * 14,
];
const is24HourFormat = () => {
  const formatter = new Intl.DateTimeFormat(undefined, { hour: 'numeric' });
  const formattedTime = formatter.format(new Date());
  return !formattedTime.includes('AM') && !formattedTime.includes('PM');
};

function formatDateIntl(date: Date): string {
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatTimeOnly(date: Date): string {
  return new Intl.DateTimeFormat(navigator.language, {
    timeStyle: 'short',
  }).format(date);
}

function formatDateOnly(date: Date): string {
  return new Intl.DateTimeFormat(navigator.language, {
    dateStyle: 'medium',
  }).format(date);
}

const formatDuration = (val: number, locale: Locale) => {
  if (val < 24) {
    return durationHours({ count: val }, { locale });
  }
  return durationDays({ count: val / 24 }, { locale });
};

const Results = ({
  language,
  onSelect,
  term,
  isLoading,
}: {
  language: Locale;
  onSelect: (val: FoundLocation) => void;
  term?: string;
  isLoading?: boolean;
}) => {
  const [searchInput, setSearchInput] = useState<string>(term ?? '');
  const [debouncedSearchInput] = useDebounce(searchInput, 150);

  useEffect(() => {
    setSearchInput(term ?? '');
  }, [term]);

  const { mutate, isPending, data, error, isError } =
    systemRestClient.findLocation.useMutation();

  useEffect(() => {
    mutate({
      body: { term: debouncedSearchInput },
      query: { locale: language },
    });
  }, [debouncedSearchInput]);

  const resolve = (val: FoundLocation) => {
    if (!isLoading) {
      onSelect(val);
    }
  };
  return (
    <Command
      shouldFilter={false}
      className='m-0 h-full w-full flex-1 bg-transparent'
    >
      <CommandInput
        disabled={isLoading}
        value={searchInput}
        className='text-base font-medium'
        onValueChange={(val) => setSearchInput(val)}
        placeholder={locationSearchPlaceholder()}
      />
      {isError && (error as any)?.body?.message && (
        <Alert variant={'destructive'} className='mt-4'>
          <CircleX className='h-4 w-4' />
          <AlertTitle>{(error as any)?.body?.message}</AlertTitle>
        </Alert>
      )}

      <CommandList className='h-full max-h-max bg-transparent'>
        <CommandEmpty>
          {!isPending && data?.body.items?.length === 0 && (
            <div className='flex items-center justify-center py-4'>
              {crudNotFound()}
            </div>
          )}
        </CommandEmpty>
        <CommandGroup className={cn('relative h-auto', { hidden: !isPending })}>
          <div className='relative flex items-center justify-center gap-2 px-2 py-2 uppercase text-muted-foreground'>
            <Loader2 className='h-8 w-8 animate-spin' />
          </div>
        </CommandGroup>
        {data?.body.items && !isPending && data?.body.items?.length > 0 && (
          <CommandGroup
            heading={
              isEmpty(debouncedSearchInput)
                ? locationSearchSuggestion()
                : undefined
            }
          >
            {data?.body.items.map((item, index) => {
              return (
                <CommandPrimitive.Item
                  value={`location-${item.id}`}
                  onSelect={() => {
                    resolve(item);
                  }}
                  className={cn(
                    'my-2 flex h-max cursor-pointer select-text items-center gap-2 rounded-lg border p-2 px-3 ring-primary ring-offset-2 transition duration-150 last:my-0 hover:bg-muted data-[active="true"]:border-input data-[active="true"]:ring'
                  )}
                  key={`suggestion-${index}`}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className='flex w-full flex-col items-center'>
                    <div className='flex w-full justify-between gap-2'>
                      <div className='flex w-full flex-1 items-center gap-2 text-sm font-medium'>
                        {item.countryCode && (
                          <FlagIcon
                            countryCode={item.countryCode.toUpperCase()}
                            className='h-4 w-4'
                          ></FlagIcon>
                        )}
                        {item.formatted}
                      </div>

                      <div className='flex h-8 w-8 items-center justify-center'>
                        {item.type === 'poi' ? (
                          <div className='flex items-center justify-center overflow-hidden rounded-md bg-blue-500 p-1 text-white'>
                            <Hotel className='size-4' />
                          </div>
                        ) : item.type === 'address' ? (
                          <div className='flex items-center justify-center overflow-hidden rounded-md bg-green-500 p-1 text-white'>
                            <House className='size-4' />
                          </div>
                        ) : (
                          <div className='flex items-center justify-center overflow-hidden rounded-md bg-red-500 p-1 text-white'>
                            <MapPin className='size-4' />
                          </div>
                        )}
                      </div>
                    </div>
                    {item.address && !isEmpty(debouncedSearchInput) && (
                      <span className='w-full text-xs opacity-70'>
                        {item.address}
                      </span>
                    )}
                  </div>
                </CommandPrimitive.Item>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
};

const SelectionTimeList = ({
  disabled,
  notFoundLabel = 'Keine Uhrzeit gefunden',
  currentDate,
  setCurrentDate,
  is24Hours,
  startDate,
  className,
}: {
  disabled?: boolean;
  notFoundLabel?: string;
  currentDate?: Date;
  setCurrentDate?: (val: Date) => void;
  is24Hours: boolean;
  startDate: Date;
  className?: string;
}) => {
  const [search, setSearch] = useState('');

  const timeArray = useMemo(() => {
    if (!currentDate) {
      return [];
    }

    const times: Date[] = [];
    const origDate = new Date(currentDate);
    const startTime =
      startOfDay(origDate) < new Date(startDate)
        ? new Date(startDate)
        : startOfDay(origDate);
    const endTime = endOfDay(startTime);
    while (isBefore(startTime, endTime)) {
      const formattedTime = new Date(startTime);
      const hours = startTime.getHours();
      const minutes = startTime.getMinutes();

      if (!(hours >= 1 && hours < 8) || (hours === 1 && minutes < 30)) {
        times.push(formattedTime);
      }

      startTime.setTime(startTime.getTime() + 30 * 60 * 1000);
    }

    return times;
  }, [is24Hours, currentDate]);

  return (
    <Command className='bg-transparent'>
      <CommandInput value={search} onValueChange={setSearch} />
      <CommandList className={cn('max-h-[200px] sm:max-h-[300px]', className)}>
        <CommandEmpty className='p-4'>{notFoundLabel}</CommandEmpty>
        <CommandGroup>
          {timeArray.map((item, index) => (
            <CommandItem
              key={`time-${index}`}
              disabled={disabled}
              value={item.toString()}
              onSelect={() => {
                setCurrentDate?.(item);
              }}
            >
              <div className='flex w-full items-center gap-2 text-base'>
                <span>{format(item, is24Hours ? 'HH:mm' : 'hh:mm')}</span>
                <span className='text-muted-foreground'>
                  {is24Hours ? 'Uhr' : format(item, 'aa')}
                </span>
              </div>
              <Check
                className={cn(
                  'ml-auto',
                  item?.toString() === currentDate?.toString()
                    ? 'opacity-100'
                    : 'opacity-0'
                )}
              ></Check>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

const DurationList = ({
  disabled,
  locale,
  notFoundLabel = 'Keine Duration gefunden',
  currentDuration,
  setCurrentDuration,
  className,
}: {
  disabled?: boolean;
  notFoundLabel?: string;
  currentDuration?: number;
  setCurrentDuration?: (val: number | undefined) => void;
  locale: Locale;
  className?: string;
}) => {
  const [search, setSearch] = useState('');

  const list = useMemo(() => {
    return allowedDurations.map((df) => {
      return {
        value: df,
        label: formatDuration(df, locale),
      };
    });
  }, [locale]);

  return (
    <Command className='bg-transparent'>
      <CommandInput value={search} onValueChange={setSearch} />
      <CommandList className={cn('bg-transparent', className)}>
        <CommandEmpty className='p-4'>{notFoundLabel}</CommandEmpty>
        <CommandGroup>
          {list.map((item) => {
            return (
              <CommandItem
                key={`duration-${item.value}`}
                disabled={disabled}
                value={item.label}
                onSelect={() => {
                  setCurrentDuration?.(
                    item.value === currentDuration ? undefined : item.value
                  );
                }}
              >
                <div className='flex w-full items-center gap-2 text-base'>
                  <span>{item.label}</span>
                </div>

                <Check
                  className={cn(
                    'ml-auto',
                    item.value === currentDuration ? 'opacity-100' : 'opacity-0'
                  )}
                ></Check>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

export const correctDateTime = (initDate: Date | string): Date => {
  let date =
    typeof initDate === 'string' ? new Date(initDate) : new Date(initDate);
  date.setSeconds(0);
  date.setMilliseconds(0);

  const minutes = date.getMinutes();
  if (minutes < 30) {
    date.setMinutes(30);
  } else {
    date.setHours(date.getHours() + 1);
    date.setMinutes(0);
  }

  const now = new Date();
  const earliest = new Date(now.getTime() + hoursInFuture * 60 * 60 * 1000);
  if (date < earliest) {
    const diff = earliest.getTime() - date.getTime();
    const steps = Math.ceil(diff / (30 * 60 * 1000));
    date = new Date(date.getTime() + steps * 30 * 60 * 1000);
  }

  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  if (totalMinutes >= 90 && totalMinutes < 480) {
    return correctDateTime(date);
  }

  return date;
};

const systemRestClient = generateRestClient(geoContract);

export type LocationSearchProps = {
  selected?: {
    name: string;
    countryCode: string;
    lng: number;
    lat: number;
    type: 'address' | 'hotel' | 'country';
  };
};

export type LocationSearchChangeEvent = {
  startDate?: Date;
  duration?: number;
  location?: FoundLocationReturn;
};

type LocationSearchInternalProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onSelect' | 'onDurationChange'
> & {
  withSubmit?: boolean;
  duration?: number;
  onSubmit?: () => void;
  value?: Date;
  location?: FoundLocationReturn;
  language: Locale;
  onSelect: (val: FoundLocation) => void;
  startDate: Date;
  onDateChange: (val: Date | undefined) => void;
  is24Hours: boolean;
  handleDateSelect: (date: Date | undefined) => void;
  onDurationChange: (duration: number | undefined) => void;
  readLocationFromBrowser?: () => void;
  isLoading?: boolean;
};

const LocationSearchDesktop = React.forwardRef<
  HTMLDivElement,
  LocationSearchInternalProps
>(
  (
    {
      withSubmit = true,
      value,
      startDate,
      duration,
      onDateChange,
      onSubmit,
      location,
      language,
      is24Hours,
      onSelect,
      handleDateSelect,
      onDurationChange,
      readLocationFromBrowser,
      className,
      isLoading,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [open, setOpen] = useState(false);
    const [openDuration, setOpenDuration] = useState(false);
    const [openTime, setOpenTime] = useState(false);

    return (
      <div
        ref={ref}
        className={cn(
          'relative grid w-full grid-cols-[1fr_1fr_min-content] flex-wrap items-center gap-4 self-stretch rounded-xl border bg-background px-2 lg:shadow-[0_8px_24px_rgba(0,0,0,0.1)] dark:lg:shadow-[0_8px_24px_rgba(0,0,0,0.4)]',
          className
        )}
      >
        <Dialog
          open={isOpen}
          onOpenChange={(val) => {
            if (!isLoading) {
              setIsOpen(val);
            }
          }}
        >
          <DialogTrigger asChild>
            <div
              className={cn(
                'relative flex h-full flex-1 items-center justify-between gap-1 border-b bg-transparent px-2 py-4 sm:border-b-0 sm:border-r'
              )}
            >
              <div className='relative flex flex-1 flex-col flex-nowrap gap-1'>
                <Label
                  htmlFor='dateWhere'
                  className='flex items-center gap-2 px-2 pt-2 text-sm font-semibold uppercase text-muted-foreground'
                >
                  {!location && (
                    <span className='relative flex size-3'>
                      <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-75'></span>
                      <span className='relative inline-flex size-3 rounded-full bg-pink-500'></span>
                    </span>
                  )}
                  {locationSearchWhereDesktop()}
                </Label>
                {isLoading && (
                  <Spinner className='absolute right-2 top-2 z-[4] size-6'></Spinner>
                )}
                <div className='flex w-full items-center justify-between'>
                  <div className='w-full flex-1'>
                    <Button
                      disabled={isLoading}
                      variant={'ghost'}
                      className={cn(
                        'relative h-auto w-full max-w-full items-center justify-start whitespace-normal border-0 bg-transparent px-2 text-left text-base text-foreground',
                        {
                          'text-muted-foreground': !location?.name,
                        }
                      )}
                    >
                      <span className={cn('line-clamp-1 break-all')}>
                        {location?.name ?? locationSearchPlaceholder()}
                      </span>
                    </Button>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        disabled={isLoading}
                        onClick={(ev) => {
                          ev.preventDefault();
                          readLocationFromBrowser?.();
                        }}
                        size={'icon'}
                        variant={'ghost'}
                      >
                        <MapPin className='h-6 w-6'></MapPin>
                      </Button>
                    </TooltipTrigger>

                    <TooltipContent>{locationSearchMyAddress()}</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          </DialogTrigger>
          <DialogContent className='flex h-full w-full flex-col sm:max-h-[500px] sm:max-w-[500px] lg:max-h-[700px] lg:max-w-[700px]'>
            <DialogHeader>
              <DialogTitle>{locationSearchWhereDesktop()}</DialogTitle>
            </DialogHeader>
            <Results
              isLoading={isLoading}
              onSelect={(val) => {
                setIsOpen(false);
                onSelect(val);
              }}
              term={location?.name}
              language={language}
            ></Results>
          </DialogContent>
        </Dialog>

        <div
          className={cn(
            'relative flex h-full flex-1 flex-nowrap items-center justify-between gap-2 px-2 py-4'
          )}
        >
          <div className='flex flex-1 flex-col gap-1'>
            <Label
              htmlFor='dateWhen'
              className='flex px-2 pt-2 text-sm font-semibold uppercase text-muted-foreground'
            >
              {!value && (
                <span className='relative flex size-3'>
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-75'></span>
                  <span className='relative inline-flex size-3 rounded-full bg-pink-500'></span>
                </span>
              )}
              {locationSearchWhenDesktop()}
            </Label>

            <div className='flex items-center justify-between gap-4'>
              <div className='flex w-full items-center gap-4'>
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={'ghost'}
                      className={cn(
                        'w-auto break-all border-0 px-2 py-4 pl-3 text-left text-base text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0',
                        !value && 'text-muted-foreground'
                      )}
                    >
                      {value && formatDateOnly(value)}
                      <CalendarIcon className='ml-auto h-4 w-4' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='w-auto p-0'>
                    <Calendar
                      startMonth={startDate}
                      disabled={{
                        before: startDate,
                      }}
                      defaultMonth={value ?? startDate}
                      showOutsideDays
                      id='dateWhen'
                      className='w-auto border-0 px-2 py-4 text-base text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0'
                      mode='single'
                      selected={value}
                      onSelect={(val) => {
                        onDateChange(val);
                        setOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Popover open={openTime} onOpenChange={setOpenTime}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={'ghost'}
                      className={cn(
                        'w-auto break-all border-0 px-2 py-4 pl-3 text-left text-base text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0',
                        !value && 'text-muted-foreground'
                      )}
                    >
                      {value && formatTimeOnly(value)}
                      <Clock className='ml-auto h-4 w-4' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='max-w-[30rem] p-0' align='start'>
                    <SelectionTimeList
                      startDate={startDate}
                      is24Hours={is24Hours}
                      setCurrentDate={(val) => {
                        handleDateSelect(val);
                        setOpenTime(false);
                      }}
                      currentDate={value}
                    ></SelectionTimeList>
                  </PopoverContent>
                </Popover>

                <Popover open={openDuration} onOpenChange={setOpenDuration}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={'ghost'}
                      className={cn(
                        'w-auto break-all border-0 px-2 py-4 pl-3 text-left text-base text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0',
                        !duration && 'text-muted-foreground'
                      )}
                    >
                      {duration
                        ? `+ ${formatDuration(duration, language)}`
                        : attendantSearchChooseDuration()}
                      <Hourglass className='ml-auto h-4 w-4' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='max-w-[30rem] p-0' align='start'>
                    <DurationList
                      locale={language}
                      currentDuration={duration}
                      setCurrentDuration={(val) => {
                        setOpenDuration(false);
                        onDurationChange(val);
                        setOpenDuration(false);
                      }}
                    ></DurationList>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </div>

        {withSubmit && (
          <div
            className={cn(
              'relative flex h-full min-h-full flex-nowrap items-center justify-between gap-2 place-self-stretch border-l px-2 pl-4'
            )}
          >
            <Button
              disabled={!value || !location}
              className='w-auto rounded-xl px-3 xl:px-4'
              size={'iconLg'}
              onClick={onSubmit}
              type='button'
            >
              <SearchIcon></SearchIcon>
              <span className='hidden xl:block'>{locationSearchSubmit()}</span>
            </Button>
          </div>
        )}
      </div>
    );
  }
);

LocationSearchDesktop.displayName = 'LocationSearchDesktop';

const LocationSearchMobile = React.forwardRef<
  HTMLDivElement,
  LocationSearchInternalProps
>(
  (
    {
      withSubmit = true,
      value,
      startDate,
      duration,
      onDateChange,
      onSubmit,
      location,
      language,
      is24Hours,
      onSelect,
      handleDateSelect,
      onDurationChange,
      readLocationFromBrowser,
      className,
      isLoading,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [open, setOpen] = useState(false);
    const [openDuration, setOpenDuration] = useState(false);

    return (
      <div ref={ref} className={cn('flex w-full flex-col gap-2', className)}>
        <Drawer
          repositionInputs={false}
          open={isOpen}
          onOpenChange={(val) => {
            if (!isLoading) {
              setIsOpen(val);
            }
          }}
        >
          <DrawerTrigger asChild>
            <div
              className={cn(
                'relative flex h-[42px] w-full rounded-lg border border-input bg-background'
              )}
            >
              <Button
                disabled={isLoading}
                variant={'outline'}
                className={cn(
                  'relative flex h-[42px] w-full max-w-full items-center justify-between gap-2 overflow-hidden rounded-none border-none bg-transparent px-2 text-left text-base'
                )}
              >
                <span className='min-w-[60px] text-muted-foreground'>
                  {locationSearchWhereMobile()}
                </span>
                <span
                  className={cn(
                    'line-clamp-1 flex-1 whitespace-normal break-all',
                    {
                      'text-muted-foreground': !location?.name,
                    }
                  )}
                >
                  {location?.name ?? locationSearchWherePlaceholder()}
                </span>
              </Button>

              <Button
                disabled={isLoading}
                className={cn({ '!opacity-0': isLoading })}
                onClick={(ev) => {
                  ev.preventDefault();
                  readLocationFromBrowser?.();
                }}
                size={'icon'}
                variant={'ghost'}
              >
                <MapPin className='h-6 w-6'></MapPin>
              </Button>
              {!location && (
                <span
                  className={cn(
                    'absolute right-[-5px] top-[-5px] flex size-3',
                    { 'opacity-0': isLoading }
                  )}
                >
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-75'></span>
                  <span className='relative inline-flex size-3 rounded-full bg-pink-500'></span>
                </span>
              )}
              {isLoading && (
                <Spinner className='absolute right-2 top-2 z-[4] size-6'></Spinner>
              )}
            </div>
          </DrawerTrigger>
          <DrawerContent className='mx-auto flex h-full max-h-[90vh] w-full max-w-screen-sm flex-col'>
            <DrawerHeader>
              <DrawerTitle>{locationSearchWhereDesktop()}</DrawerTitle>
            </DrawerHeader>
            <Results
              isLoading={isLoading}
              onSelect={(val) => {
                onSelect(val);
                setIsOpen(false);
              }}
              term={location?.name}
              language={language}
            ></Results>
          </DrawerContent>
        </Drawer>
        <Drawer repositionInputs={false} open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild>
            <Button
              variant={'outline'}
              className={cn(
                'relative flex h-[42px] w-full max-w-full items-center justify-between gap-2 px-2 text-left text-base'
              )}
            >
              <span className='min-w-[60px] text-muted-foreground'>
                {locationSearchWhenMobile()}
              </span>
              <span
                className={cn('line-clamp-1 flex-1 whitespace-normal', {
                  'text-muted-foreground': !value,
                })}
              >
                {value && formatDateIntl(value)}
              </span>
              <CalendarIcon className='ml-auto h-4 w-4' />

              {!value && (
                <span className='absolute right-[-5px] top-[-5px] flex size-3'>
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-75'></span>
                  <span className='relative inline-flex size-3 rounded-full bg-pink-500'></span>
                </span>
              )}
            </Button>
          </DrawerTrigger>
          <DrawerContent className='mx-auto flex h-full max-h-[90vh] w-full max-w-screen-sm flex-col'>
            <DrawerHeader className='text-left'>
              <DrawerTitle>{locationSearchWhenDesktop()}</DrawerTitle>
            </DrawerHeader>
            <Calendar
              defaultMonth={value ?? startDate}
              startMonth={startDate}
              disabled={{
                before: startDate,
              }}
              id='dateWhen'
              className='w-auto border-0 px-2 py-4 text-base font-bold text-foreground ring-0 hover:ring-0 focus:ring-0 focus-visible:ring-0'
              mode='single'
              selected={value}
              onSelect={onDateChange}
              initialFocus
            />

            <div className='w-full overflow-hidden'>
              <SelectionTimeList
                className='max-h-none sm:max-h-none'
                startDate={startDate}
                is24Hours={is24Hours}
                setCurrentDate={handleDateSelect}
                currentDate={value}
              ></SelectionTimeList>
            </div>
            <DrawerFooter className='pt-2'>
              <DrawerClose asChild>
                <Button variant={'default'} type='button'>
                  {locationSearchChange()}
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>

        <Drawer
          repositionInputs={false}
          open={openDuration}
          onOpenChange={setOpenDuration}
        >
          <DrawerTrigger asChild>
            <Button
              variant={'outline'}
              className={cn(
                'relative flex h-[42px] w-full max-w-full items-center justify-between gap-2 px-2 text-left text-base'
              )}
            >
              <span className='min-w-[60px] text-muted-foreground'>
                {locationSearchDurationMobile()}
              </span>
              <span
                className={cn('line-clamp-1 flex-1 whitespace-normal', {
                  'text-muted-foreground': !duration,
                })}
              >
                {duration
                  ? `+ ${formatDuration(duration, language)}`
                  : attendantSearchChooseDuration()}
              </span>
              <Hourglass className='ml-auto h-4 w-4' />

              {!duration && (
                <span className='absolute right-[-5px] top-[-5px] flex size-3'>
                  <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-75'></span>
                  <span className='relative inline-flex size-3 rounded-full bg-pink-500'></span>
                </span>
              )}
            </Button>
          </DrawerTrigger>
          <DrawerContent className='mx-auto h-full max-h-[90vh] w-full max-w-screen-sm'>
            <DrawerHeader className='text-left'>
              <DrawerTitle>{locationSearchDurationDesktop()}</DrawerTitle>
            </DrawerHeader>
            <div className='relative w-full flex-1 overflow-scroll'>
              <DurationList
                className='max-h-none'
                locale={language}
                currentDuration={duration}
                setCurrentDuration={onDurationChange}
              ></DurationList>
            </div>
            <DrawerFooter className='pt-2'>
              <DrawerClose asChild>
                <Button variant={'default'} type='button'>
                  {locationSearchChange()}
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
        {withSubmit && (
          <Button
            className='disabled:bg-[#8b8b8d] disabled:opacity-100 dark:disabled:bg-[#858587]'
            disabled={!value || !location}
            size={'lg'}
            variant={'default'}
            onClick={onSubmit}
            type='button'
          >
            <SearchIcon></SearchIcon>
            <span>{locationSearchSubmit()}</span>
          </Button>
        )}
      </div>
    );
  }
);

LocationSearchMobile.displayName = 'LocationSearchMobile';

const LocationSearch = React.forwardRef<
  HTMLDivElement,
  Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
    withSubmit?: boolean;
    onChange?: (val: LocationSearchChangeEvent) => void;
    initDate?: Date;
    initDuration?: number;
    initLocation?: FoundLocationReturn;
    className?: string;
    onlyMobile?: boolean;
    allowLocationChange?: boolean;
  }
>(
  (
    {
      withSubmit = true,
      onChange,
      initDate,
      initDuration,
      initLocation,
      className,
      onlyMobile,
      allowLocationChange = true,
    },
    ref
  ) => {
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const { language } = useLanguage();

    const onError = () => {
      toast({
        title: 'Unable to retrieve your location, please search manually',
        variant: 'error',
      });
    };

    const readLocationFromBrowser = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(success, onError);
      } else {
        onError();
      }
    };

    const [location, setLocation] = useState<FoundLocationReturn | undefined>(
      initLocation
    );

    const startDate = useMemo(() => {
      return correctDateTime(new Date());
    }, []);

    const [value, setValue] = useState<Date | undefined>(() => startDate);
    const [duration, setDuration] = useState<number | undefined>(
      initDuration && allowedDurations.includes(initDuration)
        ? initDuration
        : undefined
    );

    useEffect(() => {
      if (initDate) {
        setValue(correctDateTime(initDate));
      }
    }, [initDate]);

    const handleDateSelect = useCallback(
      (date: Date | undefined) => {
        if (date) {
          setValue(date);
          onChange?.({ startDate: date, duration, location });
        }
      },
      [onChange, duration, location]
    );

    const onDurationChange = useCallback(
      (duration: number | undefined) => {
        setDuration(duration);
        onChange?.({ startDate: value, duration, location });
      },
      [onChange, value, location]
    );

    const { originUrl } = useAppSettings();

    const onDateChange = (val: Date | undefined) => {
      const newDate = new Date(val ?? startDate);

      newDate.setSeconds(0);
      if (newDate <= startDate) {
        handleDateSelect(startDate);
        onChange?.({ startDate: startDate, duration, location });
      } else {
        if (value) {
          newDate.setHours(value.getHours());
          newDate.setMinutes(value.getMinutes());
          newDate.setSeconds(0);
        }
        handleDateSelect(newDate);
        onChange?.({ startDate: newDate, duration, location });
      }
    };

    const router = useRouter();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const is24Hours = is24HourFormat();
    const pathName = usePathname();

    const onSubmit = useCallback(() => {
      if (location && value) {
        const url = new URL(
          allowLocationChange ? location.link : pathName,
          originUrl
        );

        url.searchParams.set('country', location.country);

        if (value) {
          url.searchParams.set('start', value.toISOString());
        }
        if (duration) {
          url.searchParams.set('duration', duration.toString());
        }

        if (allowLocationChange) {
          router.push(url.pathname + url.search);
        } else {
          router.replace(url.pathname + url.search);
        }
      }
    }, [location, value, duration, originUrl, router, allowLocationChange]);

    const routeTo = (location: FoundLocationReturn) => {
      const url = new URL(
        allowLocationChange ? location.link : pathName,
        originUrl
      );
      url.searchParams.set('country', location.country);

      if (value) {
        url.searchParams.set('start', value.toISOString());
      }

      if (duration) {
        url.searchParams.set('duration', duration.toString());
      }

      if (allowLocationChange) {
        router.push(url.pathname + url.search);
      } else {
        router.replace(url.pathname + url.search);
      }
    };

    const onSelect = async (val: FoundLocation) => {
      setIsLoading(true);

      const result = await systemRestClient.resolveFindLocation.query({
        query: { language },
        params: { id: val.id },
      });

      if (result.status !== 200) {
        onError();
      } else {
        setLocation(result.body);
        onChange?.({ startDate: value, duration, location: result.body });
      }

      setIsLoading(false);
    };

    const success: PositionCallback = async (position) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      setIsLoading(true);

      const result = await systemRestClient.resolveCoordinates.query({
        query: { lng, lat, locale: language },
      });

      if (result.status === 200 && result.body !== null) {
        routeTo(result.body);
      } else {
        onError();
      }

      setIsLoading(false);
    };

    return isDesktop && onlyMobile !== true ? (
      <LocationSearchDesktop
        className={className}
        onSubmit={onSubmit}
        ref={ref}
        withSubmit={withSubmit}
        readLocationFromBrowser={readLocationFromBrowser}
        onDurationChange={onDurationChange}
        onSelect={onSelect}
        language={language}
        startDate={startDate}
        onDateChange={onDateChange}
        is24Hours={is24Hours}
        handleDateSelect={handleDateSelect}
        duration={duration}
        value={value}
        isLoading={isLoading}
        location={location}
      ></LocationSearchDesktop>
    ) : (
      <LocationSearchMobile
        onSubmit={onSubmit}
        ref={ref}
        onDurationChange={onDurationChange}
        readLocationFromBrowser={readLocationFromBrowser}
        onSelect={onSelect}
        language={language}
        className={className}
        withSubmit={withSubmit}
        startDate={startDate}
        onDateChange={onDateChange}
        is24Hours={is24Hours}
        handleDateSelect={handleDateSelect}
        duration={duration}
        value={value}
        isLoading={isLoading}
        location={location}
      ></LocationSearchMobile>
    );
  }
);
LocationSearch.displayName = 'LocationSearch';

export default LocationSearch;
